import React, { useCallback } from 'react';
import loadable from '@loadable/component';
import { Col, Grid, Row } from '@perxis-auto/ui-kit/lib/components/Grid';

import { useConstructor } from '@/context/constructor';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import {
  ConstructorType,
  IPossibleConstructorGridItem,
} from '@/interfaces/constructor.interfaces';
import { FiltersPosition } from '@/interfaces/settings.interfaces';
import ConstructorGrid from '@/layouts/Constructor/components/ConstructorGrid';

import { CatalogProps } from './Catalog.types';
import { FilterHistory } from './components';

import styles from './Catalog.module.scss';

const Filters = loadable(() => import('@/blocks/catalog/Filters'));
const CarList = loadable(() => import('@/blocks/catalog/CarList'));
const ModelList = loadable(() => import('@/blocks/catalog/ModelList'));

function Catalog(props: CatalogProps) {
  const { grid: catalogGrid } = useConstructor();

  /* Selectors */
  const filtersPosition = useAppSelector(rootSelectors.config.filtersPositionSelector);
  const isEnableFilters = useAppSelector(rootSelectors.config.filtersEnableSelector);
  const isFilterPanelShow = useAppSelector(rootSelectors.choice.showFiltersSelector);
  const isFilterDataMissing = useAppSelector(
    rootSelectors.filters.isFiltersDataMissingSelector,
  );

  const isVisibleFilters = isEnableFilters && !isFilterDataMissing && isFilterPanelShow;

  const renderFilters = useCallback((item: IPossibleConstructorGridItem) => {
    const { type } = item;
    switch (type) {
      case ConstructorType.Filters: {
        return <Filters />;
      }
      default:
        return null;
    }
  }, []);

  const renderList = useCallback((item: IPossibleConstructorGridItem) => {
    const { type, props } = item;
    switch (type) {
      case ConstructorType.CarList: {
        return (
          <Row gutter={[0, 'vertical-sky']}>
            <Col span={24}>
              <FilterHistory />
            </Col>
            <Col span={24}>
              <CarList />
            </Col>
          </Row>
        );
      }
      case ConstructorType.ModelList: {
        return <ModelList {...props} />;
      }
      default:
        return null;
    }
  }, []);

  return (
    <Row wrap={false} className={styles.catalog}>
      {isEnableFilters && (
        <Col
          xs={0}
          sm={0}
          md={isVisibleFilters ? 12 : 0}
          lg={isVisibleFilters ? 8 : 0}
          xl={isVisibleFilters ? 6 : 0}
          xxl={isVisibleFilters ? 5 : 0}
          order={filtersPosition === FiltersPosition.Left ? 1 : 2}
          className={styles.filtersCol}
        >
          <Grid
            grid={catalogGrid}
            renderItem={renderFilters}
            renderGrid={grid => (
              <ConstructorGrid grid={grid} renderItem={renderFilters} />
            )}
          />
        </Col>
      )}
      <Col
        xs={24}
        sm={24}
        md={isVisibleFilters ? 12 : 24}
        lg={isVisibleFilters ? 16 : 24}
        xl={isVisibleFilters ? 18 : 24}
        xxl={isVisibleFilters ? 19 : 24}
        order={filtersPosition === FiltersPosition.Left ? 2 : 1}
        className={styles.listCol}
      >
        <Grid
          grid={catalogGrid}
          renderItem={renderList}
          renderGrid={grid => <ConstructorGrid grid={grid} renderItem={renderList} />}
        />
      </Col>
    </Row>
  );
}

export default Catalog;
